import { useEffect, useMemo, useRef, useState } from "react";

export default function ChatBar({ selectedContact, onContactSelect, contacts }) {
    const [searchRaw, setSearchRaw] = useState("");
    const [search, setSearch] = useState("");
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        searchTimeoutRef.current = setTimeout(() => {
            setSearch(searchRaw);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
        };
    }, [searchRaw]);

    const filteredContacts = useMemo(() => {
        if (!search.trim()) return contacts;
        const s = search.trim().toLowerCase();
        return contacts.filter(
            (c) =>
                c.name?.toLowerCase().includes(s) ||
                c.email?.toLowerCase().includes(s) ||
                c.phone?.toString().includes(s)
        );
    }, [contacts, search]);

    return (
        <div className="w-full min-[1000px]:w-[360px] min-[1000px]:min-w-[360px] min-[1000px]:flex-shrink-0 min-h-0 overflow-hidden max-[999px]:h-[calc(100dvh-4rem)] min-[1000px]:h-[calc(100dvh-1rem)]">
            <aside className="flex h-full min-h-0 flex-col p-4 pt-0 lg:col-span-1">
                <div className="flex gap-2 w-full items-center mb-6 pt-4 flex-shrink-0">
                    <div className="relative flex-1">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                            <img
                                src="../chatbox/magnifer.svg"
                                alt="magnifer"
                                className="w-5 h-5"
                            />
                        </div>
                        <input
                            id="search"
                            type="search"
                            value={searchRaw}
                            onChange={(e) => setSearchRaw(e.target.value)}
                            placeholder="Search"
                            className="w-full rounded-[20px] border border-gray-400 bg-white pl-12 pr-12 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-md shadow-[#7C4DFF] [appearance:textfield] [&::-webkit-search-cancel-button]:hidden"
                        />
                        {searchRaw && (
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setSearchRaw("")}
                                aria-label="Clear search"
                            >
                                <img src="../chatbox/close.svg" alt="close" className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="space-y-3 flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:hidden">
                    {filteredContacts.map((contact) => (
                        <div
                            key={contact.id}
                            onClick={() => onContactSelect(contact)}
                            className={`flex items-center gap-3 p-3 py-2 shadow-[1px_4px_4px_0_rgba(117,117,117,0.55)] rounded-[100px] cursor-pointer transition-all duration-200 ${
                                selectedContact?.id === contact.id
                                    ? "bg-[#4F46E5] bg-opacity-20 border-[#4F46E5]"
                                    : "bg-[#797C7B] bg-opacity-[11%] hover:bg-opacity-20"
                            }`}
                        >
                            <img
                                src={contact.avatar}
                                alt={contact?.name}
                                className="h-10 w-10 rounded-full object-cover"
                            />
                            <div className="min-w-0 flex-1">
                                <p className="heading-font truncate">
                                    {contact?.name}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <img
                                    src={contact.isUnread ? "../groupscreen/phonebook.svg" : "../chatbox/phonebook.svg"}
                                    className="w-5 h-5"
                                    alt="status"
                                />
                            </div>
                        </div>
                    ))}
                    {filteredContacts.length === 0 && search.trim() && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            No contacts found. Try a different search.
                        </p>
                    )}
                </div>
            </aside>
        </div>
    );
}
